import { apiService, isAxiosError } from "api/apiService";
import { shouldFetch } from "shared/utils";
import useHelpers, { mappedTerms } from "composables/useHelpers";
import { cloneDeep } from "lodash";
import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";

/**
 * Settings Store
 *
 * This store manages the state and actions related to settings.
 */
export const useSettingsStore = defineStore("settings", () => {
  const { schoolId } = useHelpers();

  const initialSchoolSettings = reactive({});
  const initialRestrictedSchoolSettings = reactive({});
  const schoolSettings = reactive({});
  const restrictedSchoolSettings = reactive({});
  const initialUserSettings = reactive({});
  const userSettings = reactive({});
  const educationLevels = ref([]);
  const portfolioTags = ref([]);
  const initialPortfolioTags = ref([]);
  const portfolioSkills = ref([]);
  const initialPortfolioSkills = ref([]);
  const portfolioCategories = ref([]);
  const initialPortfolioCategories = ref([]);
  const ratingTypes = ref([]);
  const initialRatingTypes = ref([]);

  const fetchingData = ref(false);
  const updatingData = ref(false);

  const isFetchingUserSettings = ref(false);

  const hasFetched = ref({
    ratingTypes: false,
  });

  /**
   * Email form list.
   * @type {import('vue').Ref<Array<Object>>}
   */
  const emailFormList = ref([
    {
      key: "email_sync",
      label: "Synchronisatie fouten",
    },
    {
      key: "email_help",
      label: "Help formulieren",
    },
    {
      key: "email_license",
      label: "Licentie berichten",
    },
  ]);

  // Computed properties
  /**
   * Returns boolean when use categories for skills is enabled.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const usePortfolioCategories = computed(() => {
    return initialSchoolSettings.portfolio_settings.settings
      .allow_skill_categories;
  });

  /**
   * Returns boolean when use skills for portfolio is enabled.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const usePortfolioSkills = computed(() => {
    return initialSchoolSettings.portfolio_settings.settings
      .allow_portfolio_skills;
  });

  /**
   * Computes the names of education levels that do not have associated skills.
   * @returns {string[]}
   */
  const educationLevelsWithoutSkills = computed(() => {
    if (
      portfolioSkills.value.length === 0 ||
      educationLevels.value.length === 0
    ) {
      return [];
    }

    const levelsWithSkills = new Set(
      portfolioSkills.value
        .filter(({ meta }) => meta.archived === false)
        .flatMap((skill) => skill.education_levels),
    );

    return educationLevels.value
      .filter((level) => !levelsWithSkills.has(level.name))
      .map((level) => level.name);
  });

  /**
   * Check if grades are enabled for the school.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const gradesEnabled = computed(() => {
    return !!(
      initialSchoolSettings.student_meeting_settings.settings.enable_se_grade ||
      initialSchoolSettings.student_meeting_settings.settings
        .enable_rapport_grade
    );
  });

  /**
   * Returns an array of the allowed file types for the portfolio.
   * @type {import('vue').ComputedRef<string[]>}
   */
  const allowedPortfolioMimeTypes = computed(() => {
    const { fileTypesToMimeTypes, imageMimeTypeExtensions } = useHelpers();
    const { disallowed_file_types } =
      initialSchoolSettings.portfolio_settings.settings;

    const allowedTypes = disallowed_file_types.filter((type) => type.allowed);

    const mimeTypes = fileTypesToMimeTypes(
      allowedTypes.map(({ label }) => label),
    );

    // Image mime types are hardcoded because they are not part of the school's disallowed_file_types setting.
    // Video types are in that setting, so they come from `mimeTypes` like the documents do.
    return [...mimeTypes, ...Object.keys(imageMimeTypeExtensions)];
  });

  /**
   * Show banners in the settings.
   * @type {import('vue').ComputedRef<Array<Object>>}
   */
  const portfolioBanners = computed(() => {
    return [
      {
        type: "warning",
        message: `Categorieën staan ingeschakeld maar er zijn nog vaardigheden zonder categorie. Koppel hier een categorie aan onder "vaardigheden beheren".`,
        show:
          !!usePortfolioCategories.value &&
          portfolioSkills.value.some((skill) => skill.category_id === null),
      },
      {
        type: "warning",
        message: `Let op: Je hebt vaardigheden ingeschakeld, maar aan de volgende leerjaren zijn nog geen vaardigheden toegekend: ${educationLevelsWithoutSkills.value.join(", ")}. ${mappedTerms.students} uit deze leerjaren kunnen daardoor geen activiteit toevoegen aan hun portfolio.`,
        show:
          usePortfolioSkills.value && educationLevelsWithoutSkills.value.length,
      },
    ];
  });

  /**
   * Refetch portfolio skills when allow_portfolio_skills is enabled
   * because if the option gets triggered from false to true every skill is linked to every education level
   */
  watch(initialSchoolSettings, async () => {
    if (
      initialSchoolSettings.portfolio_settings.settings.allow_portfolio_skills
    ) {
      await getPortfolioSkillsWithArchived({
        forceRefetch: true,
      });
    }
  });

  // Methods
  /**
   * Formats the school email data.
   * @param {Object} email_settings - The email settings.
   * @returns {Array<Object>} The formatted email data.
   */
  const formatSchoolEmailData = (email_settings) => {
    const emails = [];

    const emailsByType = emailFormList.value
      .flatMap(({ key }) => key)
      .map((type) => ({
        type,
        emails: !email_settings[type]
          ? []
          : [
              ...new Set(
                email_settings[type].split(",").map((email) => email.trim()),
              ),
            ],
      }));

    emailsByType.forEach(({ type, emails: _emails }) => {
      _emails.forEach((email) => {
        const found = emails.find(({ email: _email }) => _email === email);

        if (found) {
          return !found.enabledCategories.includes(type)
            ? found.enabledCategories.push(type)
            : null;
        }

        return emails.push({
          email,
          enabledCategories: [type],
        });
      });
    });

    return emails;
  };

  // Actions

  /**
   * Fetch the school settings by school id.
   * @param {number|null} id - The school ID.
   * @param {boolean} forceRefetch - Whether to force refetch the data.
   * @returns {Promise<Object>} The school settings.
   */
  const getSchoolSettings = async (id = null, forceRefetch = false) => {
    id = id || useHelpers().schoolId;

    if (Object.keys(schoolSettings).length !== 0 && !forceRefetch) {
      return;
    }

    try {
      fetchingData.value = true;

      const {
        data: { data: schoolData },
      } = await apiService.get(`/api/school/${id}/settings`);

      schoolData.formatted_email_settings = formatSchoolEmailData(
        schoolData.email_settings,
      );

      Object.assign(schoolSettings, schoolData);

      localStorage.setItem("school_settings", JSON.stringify(schoolData));

      // Store the initial school settings before updating without Object reference
      Object.assign(
        initialSchoolSettings,
        JSON.parse(JSON.stringify(schoolData)),
      );

      return schoolData;
    } finally {
      fetchingData.value = false;
    }
  };

  /**
   * Fetches the user settings.
   *
   * @param {Object} [options={}] - The options for fetching user settings.
   * @param {boolean} [options.force=false] - Whether to force refetch the data.
   * @returns {Promise<Object>} The user settings.
   */
  const getUserSettings = async ({ force = false } = {}) => {
    if (isFetchingUserSettings.value && !force) {
      return isFetchingUserSettings.value;
    }

    isFetchingUserSettings.value = (async () => {
      try {
        const { data } = await apiService.get(`/api/settings`);

        Object.assign(userSettings, data.data);
        Object.assign(
          initialUserSettings,
          JSON.parse(JSON.stringify(data.data)),
        );

        return userSettings;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      } finally {
        isFetchingUserSettings.value = false;
      }
    })();

    return isFetchingUserSettings.value;
  };

  /**
   * Get the education levels.
   * @returns {Promise<Array<Object>>} The education levels.
   */
  const getEducationLevels = async () => {
    if (Object.keys(educationLevels.value).length !== 0) {
      return;
    }

    try {
      fetchingData.value = true;

      const { data } = await apiService.get(
        `/api/school/${schoolId}/education-levels`,
      );

      educationLevels.value = data;

      return educationLevels.value;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error);
      }
    } finally {
      fetchingData.value = false;
    }
  };

  /**
   * Get the portfolio tags.
   * @param {Object} options - The options for fetching portfolio tags.
   * @param {boolean} [options.forceRefetch=false] - Whether to force refetch the data.
   * @returns {Promise<Array<Object>>} The portfolio tags.
   */
  const getPortfolioTagsWithArchived = async (options = {}) => {
    const { forceRefetch = false } = options;

    if (portfolioTags.value.length !== 0 && !forceRefetch) {
      return;
    }

    try {
      fetchingData.value = true;

      const { data } = await apiService.get(
        `/api/school/${schoolId}/portfolio-tags`,
        {
          params: {
            archived: true,
          },
        },
      );

      portfolioTags.value = data.data;
      initialPortfolioTags.value = JSON.parse(JSON.stringify(data.data));

      return portfolioTags;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error);
      }
    } finally {
      fetchingData.value = false;
    }
  };

  /**
   * Fetch the rating types.
   * @param {boolean} forceFetch - Whether to force fetch the data.
   * @returns {Promise<Array<Object>>} The rating types.
   */
  const fetchRatingTypes = async (forceFetch = false) => {
    const { schoolId } = useHelpers();

    if (!shouldFetch(hasFetched.value["ratingTypes"]) && !forceFetch) {
      return ratingTypes.value || [];
    }

    try {
      const {
        data: { data: fetchedRatingTypes },
      } = await apiService.get(`/api/school/${schoolId}/rating-types`);

      hasFetched.value["ratingTypes"] = new Date();

      ratingTypes.value = fetchedRatingTypes;
      initialRatingTypes.value = cloneDeep(ratingTypes.value);

      return ratingTypes.value;
    } catch (error) {
      console.log("🚀 ~ fetchTeachers ~ error:", error);
      if (isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Store a rating type.
   * @param {Object} rating - The rating type to store.
   * @returns {Promise<Object>} The stored rating type.
   */
  const storeRatingType = async (rating) => {
    console.log("🚀 ~ storeRatingType ~ rating:", rating);
    try {
      const { data } = await apiService.post(
        `/api/school/${schoolId}/rating-types`,
        rating,
      );

      console.log("🚀 ~ storeRatingType ~ data:", data);

      await fetchRatingTypes(true);

      // initialPortfolioTags.value.push(data);
      // portfolioTags.value.push(data);

      return data;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    }
  };

  /**
   * Update a rating type.
   * @param {Object} ratingType - The rating type to update.
   * @returns {Promise<Object>} The updated rating type.
   */
  const updateRatingType = async (ratingType) => {
    try {
      updatingData.value = true;

      const { id } = ratingType;

      const { data } = await apiService.put(
        `/api/school/${schoolId}/rating-types/${id}`,
        ratingType,
      );

      const found = ratingTypes.value.find(({ id: _id }) => _id === id);
      const foundInitial = initialRatingTypes.value.find(
        ({ id: _id }) => _id === id,
      );

      if (found) {
        Object.assign(found, data);
      }

      if (foundInitial) {
        Object.assign(foundInitial, data);
      }

      return data;
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Delete a rating type.
   * @param {number} id - The ID of the rating type to delete.
   * @returns {Promise<Array<Object>>} The updated list of rating types.
   */
  const deleteRatingType = async (id) => {
    try {
      await apiService.delete(`/api/school/${schoolId}/rating-types/${id}`);

      return await fetchRatingTypes(true);
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    }
  };

  /**
   * Add portfolio tags.
   * @param {Object} tag - The tag to add.
   * @param {string} tag.name - The name of the tag.
   * @returns {Promise<Object>} The added tag.
   */
  const addPortfolioTags = async ({ name }) => {
    try {
      updatingData.value = true;

      const { data } = await apiService.post(
        `/api/school/${schoolId}/portfolio-tags`,
        {
          name,
        },
      );

      initialPortfolioTags.value.push(data);
      portfolioTags.value.push(data);

      return data;
    } catch (error) {
      Object.assign(
        portfolioTags,
        JSON.parse(JSON.stringify(initialPortfolioTags)),
      );

      throw error;
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Update the education levels of a portfolio tag.
   * @param {Object} tag - The tag to update.
   * @param {number} tag.id - The ID of the tag.
   * @param {Array<number>} tag.education_levels - The education levels to update.
   * @returns {Promise<void>}
   */
  const updatePortfolioTagEducationLevels = async (tag) => {
    const { id, education_levels } = tag;

    try {
      await apiService.post(
        `/api/school/${schoolId}/portfolio-tags/${id}/connect-education-levels`,
        { education_levels },
      );
    } catch (error) {
      if (isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Update the education levels of a portfolio skill.
   * @param {Object} skill - The skill to update.
   * @param {number} skill.id - The ID of the skill.
   * @param {Array<number>} skill.education_levels - The education levels to update.
   * @returns {Promise<void>}
   */
  const updatePortfolioSkillsEducationLevels = async (skill) => {
    const { id, education_levels } = skill;

    try {
      await apiService.post(
        `/api/school/${schoolId}/portfolio-skills/${id}/connect-education-levels`,
        { education_levels },
      );
    } catch (error) {
      if (isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Update a portfolio tag.
   * @param {Object} tag - The tag to update.
   * @param {number} tag.id - The ID of the tag.
   * @param {string} tag.name - The name of the tag.
   * @returns {Promise<Object>} The updated tag.
   */
  const updatePortfolioTag = async ({ id, name }) => {
    try {
      updatingData.value = true;

      const { data } = await apiService.put(
        `/api/school/${schoolId}/portfolio-tags/${id}`,
        {
          id,
          name,
        },
      );

      const found = portfolioTags.value.find(
        ({ id: portfolioId }) => portfolioId === id,
      );

      if (found) {
        Object.assign(found, data);
      }

      return data;
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        console.log(error);

        // Set the value back because the request failed
        portfolioTags.value = JSON.parse(
          JSON.stringify(initialPortfolioTags.value),
        );

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Delete a portfolio tag.
   * @param {Object} tag - The tag to delete.
   * @param {number} tag.id - The ID of the tag.
   * @returns {Promise<Array<Object>>} The updated list of portfolio tags.
   */
  const deletePortfolioTag = async ({ id }) => {
    try {
      updatingData.value = true;

      const response = await apiService.delete(
        `/api/school/${schoolId}/portfolio-tags/${id}`,
        {
          id,
        },
      );

      portfolioTags.value = portfolioTags.value.filter(
        ({ id: _id }) => _id !== id,
      );
      initialPortfolioTags.value = initialPortfolioTags.value.filter(
        ({ id: _id }) => _id !== id,
      );

      return response.data.data;
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  const archivePortfolioTag = async (tag) => {
    const {
      id,
      meta: { archived },
    } = tag;

    try {
      updatingData.value = true;

      const { data } = await apiService.put(
        `/api/school/${schoolId}/portfolio-tags/${id}/${archived ? "restore" : "archive"}`,
        {
          id,
        },
      );

      const tagIndex = portfolioTags.value.findIndex(
        ({ id: _id }) => _id === id,
      );
      const initialTagIndex = initialPortfolioTags.value.findIndex(
        ({ id: _id }) => _id === id,
      );
      if (tagIndex !== -1) {
        portfolioTags.value[tagIndex] = data;
      }
      if (initialTagIndex !== -1) {
        initialPortfolioTags.value[initialTagIndex] = data;
      }

      return data;
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Get the portfolio skills.
   * @param {Object} [options={}] - The options for fetching portfolio skills.
   * @param {boolean} [options.forceRefetch=false] - Whether to force refetch the data.
   * @returns {Promise<any>} The portfolio skills.
   */
  const getPortfolioSkillsWithArchived = async (options = {}) => {
    const { forceRefetch = false } = options;

    if (portfolioSkills.value.length !== 0 && !forceRefetch) {
      return;
    }

    try {
      fetchingData.value = true;

      const { data } = await apiService.get(`/api/portfolio/skills`, {
        params: {
          archived: true,
        },
      });

      data.data.forEach((skill) => {
        if (skill.education_levels === null) {
          skill.education_levels = [];
        }
      });

      portfolioSkills.value = data.data;
      initialPortfolioSkills.value = JSON.parse(JSON.stringify(data.data));

      return portfolioSkills;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error);
      }
    } finally {
      fetchingData.value = false;
    }
  };

  /**
   * Add portfolio skills.
   * @param {Object} skill - The skill to add.
   * @returns {Promise<any>} The added skill.
   */
  const addPortfolioSkills = async (skill) => {
    try {
      updatingData.value = true;

      const { data } = await apiService.post(
        `/api/school/${schoolId}/portfolio-skills`,
        skill,
      );

      // Add the new skill to portfolioSkills
      portfolioSkills.value.push(data);

      // Keep the initial and current value in sync
      initialPortfolioSkills.value = JSON.parse(
        JSON.stringify(portfolioSkills.value),
      );

      return data;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);

        // Set the value back because the request failed
        portfolioSkills.value = initialPortfolioSkills.value;

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Update a portfolio skill.
   * @param {Object} skill - The skill to update.
   * @param {number} skill.id - The ID of the skill.
   * @param {string} skill.name - The name of the skill.
   * @param {string} skill.description - The description of the skill.
   * @param {number} [skill.category_id] - The category ID of the skill.
   * @returns {Promise<any>} The updated skill.
   */
  const updatePortfolioSkill = async ({
    id,
    name,
    description,
    category_id,
  }) => {
    const updateObject = {
      id,
      name,
      description,
    };

    if (usePortfolioCategories.value) {
      updateObject.category_id = category_id;
    }

    try {
      updatingData.value = true;

      const response = await apiService.put(
        `/api/school/${schoolId}/portfolio-skills/${id}`,
        {
          ...updateObject,
        },
      );

      return response.data;
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        console.log(error);

        // Set the value back because the request failed
        portfolioSkills.value = JSON.parse(
          JSON.stringify(initialPortfolioSkills.value),
        );

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Delete a portfolio skill.
   * @param {Object} skill - The skill to delete.
   * @param {number} skill.id - The ID of the skill.
   * @returns {Promise<any>} The deleted skill data.
   */
  const deletePortfolioSkill = async ({ id }) => {
    try {
      updatingData.value = true;

      await apiService.delete(
        `/api/school/${schoolId}/portfolio-skills/${id}`,
        {
          id,
        },
      );

      portfolioSkills.value = portfolioSkills.value.filter(
        ({ id: _id }) => _id !== id,
      );

      initialPortfolioSkills.value = initialPortfolioSkills.value.filter(
        ({ id: _id }) => _id !== id,
      );

      return true;
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  const archivePortfolioSkill = async (skill) => {
    try {
      const {
        id,
        meta: { archived },
      } = skill;

      updatingData.value = true;

      const { data } = await apiService.put(
        `/api/school/${schoolId}/portfolio-skills/${id}/${archived ? "restore" : "archive"}`,
        {
          id,
        },
      );

      [portfolioSkills.value, initialPortfolioSkills.value].forEach(
        (skills) => {
          const skill = skills.find(({ id: _id }) => _id === id);
          skill && Object.assign(skill, data);
        },
      );

      return data;
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Get the portfolio categories.
   * @param {Object} [options={}] - The options for fetching portfolio categories.
   * @param {boolean} [options.forceRefetch=false] - Whether to force refetch the data.
   * @returns {Promise<any>} The portfolio categories.
   */
  const getPortfolioCategories = async (options = {}) => {
    const { forceRefetch = false } = options;

    if (portfolioCategories.value.length !== 0 && !forceRefetch) {
      return;
    }

    try {
      fetchingData.value = true;

      const { data } = await apiService.get(
        `/api/school/${schoolId}/portfolio-skill-categories`,
      );

      portfolioCategories.value = data.data;
      initialPortfolioCategories.value = JSON.parse(JSON.stringify(data.data));

      return portfolioCategories;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error);
      }
    } finally {
      fetchingData.value = false;
    }
  };

  /**
   * Add a portfolio category.
   * @param {Object} category - The category to add.
   * @param {string} category.name - The name of the category.
   * @returns {Promise<any>} The added category.
   */
  const addPortfolioCategory = async ({ name }) => {
    try {
      updatingData.value = true;

      const response = await apiService.post(
        `/api/school/${schoolId}/portfolio-skill-categories`,
        {
          name,
        },
      );

      portfolioCategories.value.push(response.data);
      initialPortfolioCategories.value.push(
        JSON.parse(JSON.stringify(response.data)),
      );

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);

        // Set the value back because the request failed
        portfolioCategories.value = initialPortfolioCategories.value;

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Update a portfolio category.
   * @param {Object} category - The category to update.
   * @param {number} category.id - The ID of the category.
   * @param {string} category.name - The name of the category.
   * @returns {Promise<any>} The updated category.
   */
  const updatePortfolioCategory = async ({ id, name }) => {
    try {
      updatingData.value = true;

      const { data } = await apiService.put(
        `/api/school/${schoolId}/portfolio-skill-categories/${id}`,
        {
          id,
          name,
        },
      );

      const foundCategory = portfolioCategories.value.find(
        ({ id: categoryId }) => categoryId === id,
      );

      if (foundCategory) {
        Object.assign(foundCategory, data);
      }

      return data;
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        console.log(error);

        // Set the value back because the request failed
        portfolioCategories.value = JSON.parse(
          JSON.stringify(initialPortfolioCategories.value),
        );

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Delete a portfolio category.
   * @param {Object} category - The category to delete.
   * @param {number} category.id - The ID of the category.
   * @returns {Promise<any>} The deleted category data.
   */
  const deletePortfolioCategory = async ({ id }) => {
    try {
      updatingData.value = true;

      const response = await apiService.delete(
        `/api/school/${schoolId}/portfolio-skill-categories/${id}`,
        {
          id,
        },
      );

      portfolioCategories.value = response.data.data;
      initialPortfolioCategories.value = JSON.parse(
        JSON.stringify(response.data.data),
      );

      return response.data.data;
    } catch (error) {
      console.log(error);
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Update user settings.
   * @param {Object} settings - The settings to update.
   * @param {string} settings.key - The key of the setting.
   * @param {any} settings.val - The value of the setting.
   * @returns {Promise<any>} The updated settings.
   */
  const updateUserSettings = async ({ key, val }) => {
    try {
      updatingData.value = true;

      const response = await apiService.put(`/api/settings`, {
        field: key,
        value: val,
      });

      // Update initialSchoolSettings as it's successfully updated
      Object.assign(
        initialUserSettings,
        JSON.parse(JSON.stringify(response.data.data)),
      );

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);

        // Set the value back because the request failed
        Object.assign(
          userSettings,
          JSON.parse(JSON.stringify(initialUserSettings)),
        );

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Update the school settings.
   * @param {string} key - The key of the setting.
   * @param {any} val - The value of the setting.
   * @param {boolean} [root=false] - Whether to update the root settings.
   * @returns {Promise<any>} The updated settings.
   */
  const updateSettings = async (key, val, root = false) => {
    try {
      updatingData.value = true;

      let endpoint = `/api/school/${schoolId}/settings`;

      if (root) {
        endpoint = `/api/school/${schoolId}/settings/root`;
      }

      const response = await apiService.put(endpoint, {
        field: key,
        value: val,
      });

      // Update initialSchoolSettings as it's successfully updated
      Object.assign(
        initialSchoolSettings,
        JSON.parse(JSON.stringify(response.data.data)),
      );

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);

        // Set the value back because the request failed
        Object.assign(
          schoolSettings,
          JSON.parse(JSON.stringify(initialSchoolSettings)),
        );

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Update the school's auth\_source.
   * @param {number|string} value - The new auth\_source value.
   * @returns {Promise<any>} The updated auth\_source.
   */
  const updateAuthSource = async (value) => {
    try {
      updatingData.value = true;

      let formData = new FormData();
      formData.set("sso_authsource", value);

      await apiService.post(
        `/api/school/${schoolSettings.school_id}/update-authsource`,
        formData,
      );

      restrictedSchoolSettings.sso_authsource = value;

      // Update initialSchoolSettings as it's successfully updated
      initialRestrictedSchoolSettings.sso_authsource = value;
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  /**
   * Update the email settings.
   * @param {string} type - The type of the email setting.
   * @param {string} value - The value of the email setting.
   * @returns {Promise<any>} The updated email settings.
   */
  const updateEmailSettings = async (type, value) => {
    const formData = new FormData();
    formData.set("type", type);
    formData.set("value", value);

    try {
      updatingData.value = true;

      await apiService.post(`/api/school/${schoolId}/email-settings`, formData);

      return Promise.resolve();
    } catch (error) {
      if (isAxiosError(error)) {
        return Promise.reject(error);
      }
    } finally {
      updatingData.value = false;
    }
  };

  return {
    schoolSettings,
    restrictedSchoolSettings,
    initialSchoolSettings,
    initialRestrictedSchoolSettings,
    userSettings,
    fetchingData,
    updatingData,
    storeRatingType,
    deleteRatingType,
    updateRatingType,
    usePortfolioCategories,
    emailFormList,
    initialPortfolioTags,
    fetchRatingTypes,
    ratingTypes,
    initialRatingTypes,
    educationLevels,
    portfolioTags,
    portfolioSkills,
    initialPortfolioSkills,
    portfolioCategories,
    initialPortfolioCategories,
    portfolioBanners,
    initialUserSettings,
    getSchoolSettings,
    getPortfolioTagsWithArchived,
    getPortfolioCategories,
    addPortfolioCategory,
    updatePortfolioCategory,
    deletePortfolioCategory,
    getEducationLevels,
    getUserSettings,
    updateUserSettings,
    updatePortfolioTag,
    deletePortfolioTag,
    archivePortfolioTag,
    addPortfolioTags,
    updateSettings,
    updateEmailSettings,
    updateAuthSource,
    getPortfolioSkillsWithArchived,
    addPortfolioSkills,
    updatePortfolioSkill,
    deletePortfolioSkill,
    archivePortfolioSkill,
    updatePortfolioTagEducationLevels,
    updatePortfolioSkillsEducationLevels,
    allowedPortfolioMimeTypes,
    gradesEnabled,
  };
});
